import { useMemo } from 'react';

interface OptionType {
  value: string;
  display: string;
  extra: any;
  label: React.ReactNode;
}

interface UseFilteredProjectOptionsProps {
  options: OptionType[];
  searchValue: string;
  filterOption: (search: string, opt: OptionType) => boolean;
  isCreateModal?: boolean;
}

export const useFilteredProjectOptions = ({
  options,
  searchValue,
  filterOption,
  isCreateModal = false,
}: UseFilteredProjectOptionsProps) => {
  return useMemo(() => {
    if (!searchValue) {
      return isCreateModal ? options : options.filter((opt) => !opt.extra.project);
    }

    const lowerSearch = searchValue.toLowerCase();

    const matched: OptionType[] = [];
    const projectUuidsFromMatchedRequests = new Set<string>();

    options.forEach((opt) => {
      const isMatch = filterOption(lowerSearch, opt);

      if (isMatch) {
        if (opt.extra.project) {
          projectUuidsFromMatchedRequests.add(opt.extra.project.uuid);
        }
        matched.push(opt);
      }
    });

    const additionalProjects = [...projectUuidsFromMatchedRequests]
      .filter((uuid) => !matched.some((opt) => opt.value === uuid))
      .map((uuid) => options.find((opt) => opt.value === uuid && !opt.extra.project) as OptionType)
      .filter(Boolean);

    const filtered = [...additionalProjects, ...matched];

    const projects = filtered.filter((opt) => !opt.extra.project);
    const requests = filtered.filter((opt) => opt.extra.project);

    const result: OptionType[] = [];

    projects.forEach((project) => {
      result.push(project);

      const relatedRequests = requests.filter((req) => req.extra.project.uuid === project.value);

      result.push(...relatedRequests);
    });

    return result;
  }, [searchValue, options, filterOption, isCreateModal]);
};
